/**
 * @author
 * David Rivera (jherax)
 * https://github.com/jherax
 */

import PATTERNS from './patterns';
import fieldType from '../dom/fieldType';

/**
 * @private
 * Configures the data descriptor for a property in an object.
 *
 * @param  {Object} obj: the object to set the data descriptors
 * @param  {String} property: the property to configure
 * @param  {Any} value: sets the value descriptor for the property
 * @param  {Boolean} enumerable: (optional) if is a printable property
 * @throws {TypeError} if you pass a property with accessor descriptor (`get, set`)
 */
function setDescriptor(obj, property, value, enumerable) {
  Object.defineProperty(obj, property, {
    configurable: true,
    enumerable: !!enumerable,
    writable: false,
    value: value != null ? value : obj[property],
  });
}

/**
 * @private
 * Validates the provided text against a regular expression pattern.
 *
 * @param  {String|Element} text: the text to validate
 * @param  {RegExp} pattern: regular expression pattern
 * @return {Boolean}
 */
function validate(text, pattern) {
  text = fieldType.isWritable(text) ? text.value : text.toString();
  return pattern.test(text);
}

/**
 * @private
 * Initializes the namespace with all the built-in validators
 *
 * @return {Object}
 */
function initValidators() {
  // @namespace
  const validators = {};
  // registers the validators in the namespace
  Object.keys(PATTERNS).forEach((validatorName) => {
    setDescriptor(
      /* namespace  */ validators,
      /* property   */ validatorName,
      /* value      */ (text, pattern) => validate(text, pattern || PATTERNS[validatorName]),
      /* enumerable */ true,
    );
  });
  // registers a function to add/configure validators
  setDescriptor(validators, 'set', (validatorName, func) => {
    if (typeof func !== 'function') return;
    const callback = func('test');
    const cbType = typeof callback;
    if (cbType !== 'boolean' && cbType !== 'function') {
      throw new TypeError('The validator should return Boolean or Function');
    }
    setDescriptor(validators, validatorName, cbType === 'function' ? callback : func, true);
  });
  return validators;
}

/**
 * @public API
 */
export default initValidators();
